import { 
  SupportedLanguageCode, 
  LANGUAGE_CODES, 
  LanguageCode, 
  getTranslation,
  updateTranslation
} from './i18n'

// API仕様に完全対応した型定義
export interface ItemTranslation {
  langCode: string                // 言語コード
  name: string                    
  description: string             
  story: string                   
}

export interface ItemTasteTranslation {
  langCode: string                // 言語コード
  name: string                    
}

export interface ItemFeatureTranslation {
  langCode: string                // 言語コード
  name: string                    
  content: string                 
}

// アイテム翻訳用ヘルパー関数
export const getItemTranslation = (
  translations: ItemTranslation[] | undefined,
  selectedLanguageCode: LanguageCode
): ItemTranslation | undefined => {
  return getTranslation(translations, selectedLanguageCode)
}

export const getTasteTranslation = (
  translations: ItemTasteTranslation[] | undefined,
  selectedLanguageCode: LanguageCode
): ItemTasteTranslation | undefined => {
  return getTranslation(translations, selectedLanguageCode)
}

export const getFeatureTranslation = (
  translations: ItemFeatureTranslation[] | undefined,
  selectedLanguageCode: LanguageCode
): ItemFeatureTranslation | undefined => {
  return getTranslation(translations, selectedLanguageCode)
}

// 翻訳データ作成ヘルパー関数
export const createEmptyItemTranslations = (): ItemTranslation[] => [
  { langCode: LANGUAGE_CODES.JAPANESE, name: '', description: '', story: '' },
  { langCode: LANGUAGE_CODES.ENGLISH, name: '', description: '', story: '' },
]

export const createEmptyTasteTranslations = (): ItemTasteTranslation[] => [
  { langCode: LANGUAGE_CODES.JAPANESE, name: '' },
  { langCode: LANGUAGE_CODES.ENGLISH, name: '' },
]

export const createEmptyFeatureTranslations = (): ItemFeatureTranslation[] => [
  { langCode: LANGUAGE_CODES.JAPANESE, name: '', content: '' },
  { langCode: LANGUAGE_CODES.ENGLISH, name: '', content: '' },
]

// ItemTranslation用の更新関数
export const updateItemTranslation = (
  translations: ItemTranslation[] | undefined,
  langCode: string,
  field: 'name' | 'description' | 'story',
  value: string
): ItemTranslation[] => {
  const currentTranslations = translations || []
  return updateTranslation(currentTranslations, langCode, { [field]: value })
}

// ItemTasteTranslation用の更新関数
export const updateTasteTranslation = (
  translations: ItemTasteTranslation[] | undefined,
  langCode: string,
  name: string
): ItemTasteTranslation[] => {
  const currentTranslations = translations || []
  return updateTranslation(currentTranslations, langCode, { name })
}

// ItemFeatureTranslation用の更新関数
export const updateFeatureTranslation = (
  translations: ItemFeatureTranslation[] | undefined,
  langCode: string,
  field: 'name' | 'content',
  value: string
): ItemFeatureTranslation[] => {
  const currentTranslations = translations || []
  return updateTranslation(currentTranslations, langCode, { [field]: value })
}

// 表示用ヘルパー関数
export const getTasteDisplayName = (
  taste: { name?: string; itemTasteTranslations?: ItemTasteTranslation[] },
  langCode: SupportedLanguageCode
): string => {
  // 翻訳配列から取得を試す
  const translationResult = getTasteTranslation(taste.itemTasteTranslations, langCode)?.name
  if (translationResult) {
    return translationResult
  }

  // 翻訳がない場合、日本語なら直接フィールドから取得
  if (langCode === LANGUAGE_CODES.JAPANESE && taste.name) {
    return taste.name
  }

  return ''
}

export const getFeatureDisplayName = (
  feature: { name?: string; itemFeatureTranslations?: ItemFeatureTranslation[] },
  langCode: SupportedLanguageCode
): string => {
  // 翻訳配列から取得を試す
  const translationResult = getFeatureTranslation(feature.itemFeatureTranslations, langCode)?.name
  if (translationResult) {
    return translationResult
  }

  // 翻訳がない場合、日本語なら直接フィールドから取得
  if (langCode === LANGUAGE_CODES.JAPANESE && feature.name) {
    return feature.name
  }

  return ''
}

export const getFeatureDisplayContent = (
  feature: { content?: string; itemFeatureTranslations?: ItemFeatureTranslation[] },
  langCode: SupportedLanguageCode
): string => {
  // 翻訳配列から取得を試す
  const translationResult = getFeatureTranslation(feature.itemFeatureTranslations, langCode)?.content
  if (translationResult) {
    return translationResult
  }

  // 翻訳がない場合、日本語なら直接フィールドから取得
  if (langCode === LANGUAGE_CODES.JAPANESE && feature.content) {
    return feature.content
  }

  return ''
}